import type { FirebaseApp } from "firebase/app";
import { getToken, initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import { EnvironmentConfigurationError } from "../../packages/foundation/src/environment.js";

export type ClientAppCheckMode = "disabled" | "observe" | "enforce";
export type WebAppCheckConfiguration = {
  mode: ClientAppCheckMode;
  siteKey: string;
};

export function resolveWebAppCheckConfiguration(input: {
  environment: string;
  useEmulators: boolean;
  mode?: string;
  siteKey?: string;
}): WebAppCheckConfiguration {
  const mode = input.mode?.trim() || (input.environment === "production" ? "observe" : "disabled");
  if (!["disabled", "observe", "enforce"].includes(mode)) {
    throw new EnvironmentConfigurationError("VITE_APP_CHECK_CLIENT_MODE is invalid.");
  }
  if (input.environment === "production" && mode === "disabled") {
    throw new EnvironmentConfigurationError("Production clients must initialize App Check.");
  }
  if (input.useEmulators && mode !== "disabled") {
    throw new EnvironmentConfigurationError("Emulator clients must disable App Check.");
  }
  const siteKey = input.siteKey?.trim() ?? "";
  if (mode !== "disabled" && !/^[A-Za-z0-9_-]{20,}$/.test(siteKey)) {
    throw new EnvironmentConfigurationError("A public reCAPTCHA Enterprise site key is required.");
  }
  return { mode: mode as ClientAppCheckMode, siteKey };
}

export function initializeWebAppCheck(app: FirebaseApp, config: WebAppCheckConfiguration) {
  if (config.mode === "disabled") return async () => undefined;
  // Never accept a browser-injected debug provider in a deployable cloud client.
  if ((globalThis as { FIREBASE_APPCHECK_DEBUG_TOKEN?: unknown }).FIREBASE_APPCHECK_DEBUG_TOKEN) {
    throw new EnvironmentConfigurationError("App Check debug providers are not supported.");
  }
  const appCheck = initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(config.siteKey),
    isTokenAutoRefreshEnabled: true,
  });
  return async () => (await getToken(appCheck)).token;
}

export function createAppCheckFetch(input: {
  apiBaseUrl: string;
  projectId: string;
  useEmulators: boolean;
  mode: ClientAppCheckMode;
  getToken: () => Promise<string | undefined>;
  fetchClient?: typeof fetch;
  onUnavailable?: () => void;
  timeoutMs?: number;
  observationWaitMs?: number;
  now?: () => number;
}) {
  const base = new URL(input.apiBaseUrl);
  const expectedBase = input.useEmulators
    ? `http://127.0.0.1:5001/demo-shdeed-dev/us-central1`
    : `https://us-central1-${input.projectId}.cloudfunctions.net`;
  if (
    base.href.replace(/\/+$/u, "") !== expectedBase ||
    (input.useEmulators && input.projectId !== "demo-shdeed-dev")
  ) {
    throw new EnvironmentConfigurationError("API destination must match the Firebase project.");
  }
  const pathPrefix = `${base.pathname.replace(/\/+$/u, "")}/`;
  const fetchClient = input.fetchClient ?? fetch;
  const now = input.now ?? Date.now;
  let unavailableUntil = 0;
  let pending: Promise<string | undefined> | undefined;

  async function readToken(): Promise<string | undefined> {
    if (input.mode === "disabled") return undefined;
    if (now() < unavailableUntil) {
      if (input.mode === "enforce") throw new Error("app_check_unavailable");
      return undefined;
    }
    if (pending) return pending;
    pending = (async () => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        const token = await Promise.race([
          Promise.resolve().then(input.getToken),
          new Promise<never>((_, reject) => {
            timer = setTimeout(
              () => reject(new Error("app_check_timeout")),
              input.timeoutMs ?? 8000,
            );
          }),
        ]);
        if (!token?.trim()) throw new Error("app_check_unavailable");
        return token;
      } catch {
        unavailableUntil = now() + 30000;
        input.onUnavailable?.();
        if (input.mode === "enforce") throw new Error("app_check_unavailable");
        return undefined;
      } finally {
        clearTimeout(timer);
        pending = undefined;
      }
    })();
    return pending;
  }

  return async (url: string, init: RequestInit = {}): Promise<Response> => {
    const destination = new URL(url);
    if (
      destination.origin !== base.origin ||
      !destination.pathname.startsWith(pathPrefix) ||
      destination.username ||
      destination.password ||
      destination.hash
    ) {
      throw new Error("untrusted_api_destination");
    }
    init.signal?.throwIfAborted();
    const lookup = readToken();
    let observationTimer: ReturnType<typeof setTimeout> | undefined;
    let token: string | undefined;
    try {
      // Keep the lookup alive for SDK caching, without stalling observed traffic.
      token =
        input.mode === "observe"
          ? await Promise.race([
              lookup,
              new Promise<undefined>((resolve) => {
                observationTimer = setTimeout(
                  () => resolve(undefined),
                  input.observationWaitMs ?? 400,
                );
              }),
            ])
          : await lookup;
    } finally {
      clearTimeout(observationTimer);
    }
    init.signal?.throwIfAborted();
    const headers = new Headers(init.headers);
    headers.delete("X-Firebase-AppCheck");
    if (token) headers.set("X-Firebase-AppCheck", token);
    return fetchClient(url, { ...init, headers, redirect: "error" });
  };
}
